import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import { Reveal } from "../components/site/Motion";
import { SectionLabel } from "../components/site/Bits";
import { getPost } from "../lib/posts";
import { LOGO_URL } from "../lib/site";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export default function BlogArticle() {
  const { slug } = useParams();
  const post = getPost(slug);

  useEffect(() => {
    if (!post) return;
    const prevTitle = document.title;
    document.title = `${post.title} | RenewTracker`;
    const descEl = document.querySelector('meta[name="description"]');
    const prevDesc = descEl ? descEl.getAttribute("content") : null;
    if (descEl) descEl.setAttribute("content", post.description);

    // Article JSON-LD (only verified fields; no author invented)
    const url = `https://renewtracker.site/blog/${post.slug}`;
    const ld = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.description,
      image: LOGO_URL,
      datePublished: post.datePublished,
      dateModified: post.dateModified,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      url,
      publisher: {
        "@type": "Organization",
        name: "RenewTracker",
        logo: { "@type": "ImageObject", url: LOGO_URL },
      },
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.page = `blog-${post.slug}`;
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      if (descEl && prevDesc !== null) descEl.setAttribute("content", prevDesc);
      script.remove();
    };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const Body = post.body;

  return (
    <main data-testid="blog-article-page" className="bg-background pt-28">
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="mesh-glow pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-3xl px-5 py-14 lg:px-8">
          <Reveal>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:underline"
              data-testid="back-to-blog"
            >
              <ArrowLeft className="h-4 w-4" /> Back to blog
            </Link>
            <div className="mt-6">
              <SectionLabel>{post.tag}</SectionLabel>
            </div>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.1] tracking-tighter text-ink sm:text-5xl">
              {post.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                Published {formatDate(post.datePublished)}
              </span>
              {post.dateModified !== post.datePublished && (
                <span className="inline-flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  Updated {formatDate(post.dateModified)}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" /> {post.readTime}
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 py-12 lg:px-8">
        <Body />
      </section>
    </main>
  );
}
